import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Database, LayoutDashboard, Loader2, PencilLine } from 'lucide-react';
import { Skeleton } from 'components/ui/skeleton';
import { toast } from 'components/ui/use-toast';
import { useAuthContext } from 'modules/auth/context';
import { RecommendedTemplateRow, templateLanguageFor, useRecommendedTemplates } from 'modules/templates';
import { SeedDemoWorkspace } from 'modules/tour/api';
import { cn } from 'lib/utils';

interface YourPicksProps {
  /** The use case just chosen; steers the optional sample workspace. */
  useCase: string | null;
}

interface ExitOptionProps {
  icon: React.ElementType;
  label: string;
  description: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
}

function ExitOption({ icon: Icon, label, description, onClick, busy, disabled }: ExitOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-start gap-3 rounded-md border border-border p-3 text-left transition-colors',
        'hover:border-foreground/20 hover:bg-accent/50 disabled:cursor-not-allowed disabled:opacity-60',
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Icon className="h-4 w-4" aria-hidden />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-foreground">{label}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

/**
 * The end of staff onboarding: templates matched to the answers just given,
 * plus the other ways in. Sample data is an explicit opt-in here (it used to be
 * seeded automatically and counted against the plan's test limit).
 */
export function YourPicks({ useCase }: YourPicksProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { state, methods } = useAuthContext();
  const { data, isLoading } = useRecommendedTemplates({ language: templateLanguageFor(i18n.language), limit: 3 });
  const [seeding, setSeeding] = useState(false);

  const exploreSamples = async () => {
    const orgId = state.organization?.id;
    if (!orgId || seeding) return;
    setSeeding(true);
    try {
      const { data: result } = await SeedDemoWorkspace(orgId, { useCase: useCase ?? undefined, language: i18n.language?.slice(0, 2) });
      if (!result?.success) throw new Error('seed-failed');
      // demoTestId lets the product tour show its test-structure steps right away.
      if (state.organization) {
        methods.setOrganization({ ...state.organization, hasDemoWorkspace: true, demoTestId: result.testId });
      }
      navigate('/app', { replace: true });
    } catch {
      toast.error(t('auth.ob_picks_samples_failed'));
      setSeeding(false);
    }
  };

  const picks = data?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('auth.ob_picks_title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {data && !data.basedOnProfile ? t('auth.ob_picks_desc_popular') : t('auth.ob_picks_desc')}
        </p>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-3" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 rounded-lg" />
          ))}
        </div>
      ) : (
        <RecommendedTemplateRow
          templates={picks}
          onSelect={(template) => navigate(`/app/tests/templates?preview=${encodeURIComponent(template.id)}`, { replace: true })}
        />
      )}

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {picks.length ? t('auth.ob_picks_or') : t('auth.ob_picks_start')}
        </p>
        <div className="grid gap-2 sm:grid-cols-3">
          <ExitOption
            icon={PencilLine}
            label={t('auth.ob_picks_scratch')}
            description={t('auth.ob_picks_scratch_desc')}
            onClick={() => navigate('/app/tests/create', { replace: true })}
            disabled={seeding}
          />
          <ExitOption
            icon={Database}
            label={t('auth.ob_picks_samples')}
            description={t('auth.ob_picks_samples_desc')}
            onClick={exploreSamples}
            busy={seeding}
            disabled={seeding}
          />
          <ExitOption
            icon={LayoutDashboard}
            label={t('auth.ob_picks_dashboard')}
            description={t('auth.ob_picks_dashboard_desc')}
            onClick={() => navigate('/app', { replace: true })}
            disabled={seeding}
          />
        </div>
      </div>
    </div>
  );
}
